import React from 'react';
import { Carga, oQueMostrar } from './cargaDaLista';
import { textos } from './textos';
import { fmtDateTime } from '../utils/formatters';
import { TONE_BADGE } from '../utils/statusColors';

/**
 * O lugar da lista quando ela não é só a lista: carregando, erro ou vazio de verdade. Ver design/cargaDaLista.
 *
 * - `vazio`: o que a tela diz quando carregou e não há nada ("Nenhuma proposta cadastrada"). Só aparece com a
 *   carga PRONTA; com erro, a tela diz que não conseguiu carregar, e não que não há nada. É OBRIGATÓRIO: quando
 *   quem diz o vazio é a própria lista (`children`), como a tabela de Faturas com a linha "Nenhuma fatura
 *   encontrada", a tela passa `A_PROPRIA_LISTA_DIZ_O_VAZIO`, e a lista vazia só é mostrada com a carga pronta.
 *   Era opcional, e ausente queria dizer isso: a tela que esquecesse o `vazio` recebia em silêncio o comportamento
 *   mais fraco, sem quebrar teste nenhum. Obrigatório, o compilador cobra a escolha em toda chamada.
 * - `children`: a lista. Com erro numa RECARGA, a lista que já estava fica (erro não zera os dados) e ganha uma
 *   faixa em cima dizendo que ela é da última carga que deu certo.
 */
/** Para `vazio`: a própria lista (`children`) já tem a sua linha de vazio. Ver o comentário acima. */
export const A_PROPRIA_LISTA_DIZ_O_VAZIO = Symbol('a própria lista diz o vazio');

interface Props {
  carga: Pick<Carga, 'estado' | 'erro' | 'atualizadoEm' | 'faltaram'>;
  /** Há itens para mostrar (de agora ou da última carga boa)? */
  temDados: boolean;
  /** "as propostas", "as faturas": entra na frase do erro. */
  oQue: string;
  onTentarDeNovo: () => void;
  /** O que dizer com a carga pronta e nada a mostrar, ou `A_PROPRIA_LISTA_DIZ_O_VAZIO`. */
  vazio: React.ReactNode | typeof A_PROPRIA_LISTA_DIZ_O_VAZIO;
  children: React.ReactNode;
}

export default function EstadoDaLista({ carga, temDados, oQue, onTentarDeNovo, vazio, children }: Props) {
  const modo = oQueMostrar(carga, temDados);
  const tentar = (
    <button
      type="button"
      onClick={onTentarDeNovo}
      className="ml-2 px-2.5 py-1 rounded bg-slate-700 hover:bg-slate-600 text-slate-100 text-xs font-medium"
    >
      {textos.tentarDeNovo}
    </button>
  );

  if (modo === 'carregando') {
    return <div className="text-center py-16 text-slate-400" data-carga="carregando">{textos.carregando(oQue)}</div>;
  }
  if (modo === 'erro-sem-dados') {
    return (
      <div role="alert" className={`my-6 px-4 py-4 rounded-lg border border-red-600/50 text-sm ${TONE_BADGE.danger}`} data-carga="erro">
        <div className="font-semibold">{textos.naoConseguiCarregar(oQue)}</div>
        <div className="mt-1 text-red-200/90">{carga.erro}</div>
        <div className="mt-1 text-red-200/70 text-xs">{textos.issoNaoEVazio}</div>
        <div className="mt-3">{tentar}</div>
      </div>
    );
  }
  if (modo === 'vazio') {
    return <div data-carga="vazio">{vazio === A_PROPRIA_LISTA_DIZ_O_VAZIO ? children : vazio}</div>;
  }
  return (
    <>
      {modo === 'lista-parcial' && (
        <div role="alert" className={`mb-3 px-4 py-2.5 rounded-lg border border-amber-600/50 text-xs ${TONE_BADGE.warning}`} data-carga="parcial">
          {textos.listaIncompleta(carga.faltaram, carga.erro)}
          {tentar}
        </div>
      )}
      {modo === 'lista-com-erro' && (
        <div role="alert" className={`mb-3 px-4 py-2.5 rounded-lg border border-amber-600/50 text-xs ${TONE_BADGE.warning}`} data-carga="erro-com-lista">
          {textos.naoConseguiAtualizar(oQue, carga.erro)}
          {carga.atualizadoEm ? textos.daUltimaCargaBoa(fmtDateTime(carga.atualizadoEm)) : ''}
          {tentar}
        </div>
      )}
      {children}
    </>
  );
}
